import { useRef, useState } from 'react';
import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import type { FunnelStatus, User } from 'src/api/generated';
import type { Task } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TagSkeleton } from 'src/modules/shared/components/atoms/tag/tag-skeleton';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { TagDropdown } from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { HorizontalOverflowStrip } from 'src/modules/shared/components/molecules/horizontal-overflow-strip';
import {
  AI_STATUSES_HEADER_ID,
  buildConversationStatusDropdownOptions,
  isStatusDropdownGroupHeader,
} from 'src/modules/communications/utils/status-dropdown-options.util';
import {
  buildConversationUserOptions,
  getConversationHeaderCurrentStatusName,
  getConversationHeaderCurrentUserName,
  getConversationTaskSummary,
} from 'src/modules/communications/utils/conversation-detail.util';
import { ConversationHeaderTaskTag } from './conversation-header-task-tag';
import { TestConversationBadge } from '../../molecules/test-conversations/test-conversation-badge';

interface ConversationHeaderMetadataChipsProps {
  aiStatusIds: number[];
  funnelStatusId?: number;
  handleOpenTaskModal: () => void;
  handleStatusChange: (statusId: number | null) => Promise<void>;
  handleUserAssign: (userId: number | null) => Promise<void>;
  isTestConversation: boolean;
  isSmallScreen: boolean;
  loadingAgents: boolean;
  loadingStatuses: boolean;
  statuses: FunnelStatus[];
  tasks: Task[];
  updatingConversation: boolean;
  userId?: number;
  users: User[];
}

export const ConversationHeaderMetadataChips = ({
  aiStatusIds,
  funnelStatusId,
  handleOpenTaskModal,
  handleStatusChange,
  handleUserAssign,
  isTestConversation,
  isSmallScreen,
  loadingAgents,
  loadingStatuses,
  statuses,
  tasks,
  updatingConversation,
  userId,
  users,
}: ConversationHeaderMetadataChipsProps) => {
  const { t } = useI18n();
  const [areAiStatusesCollapsed, setAreAiStatusesCollapsed] = useState(true);
  const wasStatusDropdownOpenRef = useRef(false);
  const statusOptions = buildConversationStatusDropdownOptions(
    statuses,
    funnelStatusId,
    aiStatusIds,
    areAiStatusesCollapsed
  );
  const userOptions = buildConversationUserOptions({ t, userId, users });
  const currentStatus = statuses.find((status) => status.id === funnelStatusId);
  const currentStatusName = getConversationHeaderCurrentStatusName(
    statuses,
    funnelStatusId
  );
  const currentUserLabel = getConversationHeaderCurrentUserName({
    t,
    userId,
    users,
  });
  const { overdueTaskCount, upcomingTaskCount } =
    getConversationTaskSummary(tasks);
  const totalTaskCount = overdueTaskCount + upcomingTaskCount;
  const overflowWatchValue = [
    currentStatusName,
    currentUserLabel,
    isSmallScreen,
    loadingAgents,
    loadingStatuses,
    overdueTaskCount,
    totalTaskCount,
    upcomingTaskCount,
    userOptions.length,
    statusOptions.length,
  ].join('|');
  const metadataChipClassName =
    'flex-shrink-0 !h-auto !gap-1 !px-1.5 !py-[0.095rem] !pr-1.5 [&_svg:last-child]:h-3.5 [&_svg:last-child]:w-3.5';

  return (
    <HorizontalOverflowStrip
      className="col-span-3 px-1 pt-0.5"
      contentClassName="gap-2 pb-0.5"
      watchValue={overflowWatchValue}
    >
      {isTestConversation && (
        <TestConversationBadge
          size="xs"
          inlineContent
          className="flex-shrink-0"
        />
      )}
      {isTestConversation && (
        <div className="mx-1 my-0.5 h-4 w-px flex-shrink-0 self-center bg-gray-200" />
      )}

      <ConversationHeaderTaskTag
        handleOpenTaskModal={handleOpenTaskModal}
        isSmallScreen={isSmallScreen}
        tasks={tasks}
      />

      {loadingStatuses ? (
        <TagSkeleton size="xs" />
      ) : (
        <TagDropdown
          content={
            <FunnelStatusName
              name={currentStatusName ?? ''}
              outcome={currentStatus?.terminalOutcome}
              icon="archive"
              showArchiveTooltip
              size="tiny"
              className="max-w-[140px]"
            />
          }
          variant="primary"
          appearance="outline"
          size="2xs"
          options={statusOptions}
          mobileTitle={t('communications.dropdown.status.mobileTitle')}
          onOpenChange={(isOpen) => {
            if (isOpen && !wasStatusDropdownOpenRef.current) {
              setAreAiStatusesCollapsed(true);
            }

            wasStatusDropdownOpenRef.current = isOpen;
          }}
          onOptionSelect={(option) => {
            if (isStatusDropdownGroupHeader(option.id)) {
              if (option.id === AI_STATUSES_HEADER_ID) {
                setAreAiStatusesCollapsed((current) => !current);
              }

              return;
            }

            void handleStatusChange(option.id as number);
          }}
          isLoading={updatingConversation}
          className={metadataChipClassName}
        />
      )}

      {loadingAgents ? (
        <TagSkeleton size="xs" />
      ) : (
        <TagDropdown
          content={
            <Text
              size="tiny"
              className="max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {currentUserLabel}
            </Text>
          }
          variant="primary"
          appearance="outline"
          size="2xs"
          options={userOptions}
          mobileTitle={t('communications.dropdown.assignee.mobileTitle')}
          onOptionSelect={(option) =>
            handleUserAssign(
              option.id === 'unassign' ? null : (option.id as number)
            )
          }
          isLoading={updatingConversation}
          className={metadataChipClassName}
        />
      )}
    </HorizontalOverflowStrip>
  );
};
